import { motion } from "framer-motion";
import { Landmark, Stethoscope, Sparkles, ShieldCheck, Gamepad2, Check, Star, Calendar } from "lucide-react";
import { CLIENTS } from "@/data/site";

const INDUSTRY_ICON = {
  Government: Landmark,
  Healthcare: Stethoscope,
  FinTech: ShieldCheck,
  Gaming: Gamepad2,
  "Consumer Brand": Sparkles,
};

function ClientCard({ c, i, featured = false }) {
  const Icon = INDUSTRY_ICON[c.industry] || Sparkles;

  return (
    <motion.article
      data-testid={`client-card-${featured ? "featured" : i}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (i % 3) * 0.06, ease: [0.19, 1, 0.22, 1] }}
      className={`group relative bg-white p-8 md:p-10 flex flex-col overflow-hidden ring-1 ring-brand-border hover:ring-brand-gold/60 transition-all duration-500 ${
        featured ? "md:col-span-2 lg:col-span-3 border-l-2 border-brand-gold" : ""
      }`}
    >
      {/* Header: logo + industry + duration */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          {c.logoUrl ? (
            <span className={`${featured ? "w-20 h-20 md:w-24 md:h-24" : "h-14 min-w-[56px] max-w-[120px]"} rounded-full bg-white flex items-center justify-center overflow-hidden ring-2 ring-brand-gold/40 shrink-0`}>
              <img
                src={c.logoUrl}
                alt={`${c.name} logo`}
                className="w-full h-full object-contain p-2"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </span>
          ) : (
            <span className={`${featured ? "w-20 h-20" : "w-14 h-14"} rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold shrink-0`}>
              <Icon size={featured ? 28 : 20} strokeWidth={1.5} />
            </span>
          )}
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full chip-gradient text-[10px] uppercase tracking-[0.24em] font-bold">
              <Icon size={11} className="text-brand-gold" /> {c.industry}
            </span>
            {c.duration && (
              <p className="mt-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-brand-charcoal/60">
                <Calendar size={11} className="text-brand-brown" /> {c.duration}
              </p>
            )}
          </div>
        </div>

        {c.badge && (
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full chip-gradient ring-1 ring-brand-gold/40 text-brand-gold text-[10px] uppercase tracking-[0.24em] font-bold whitespace-nowrap">
            <Star size={11} className="fill-brand-gold text-brand-gold" /> {c.badge}
          </span>
        )}
      </div>

      {/* Name */}
      <h3 className={`font-display leading-tight text-brand-charcoal ${featured ? "text-3xl md:text-4xl lg:text-5xl" : "text-2xl md:text-[26px]"}`}>
        {c.name}
      </h3>

      {/* Description */}
      <p className={`mt-4 text-brand-charcoal/70 leading-relaxed ${featured ? "text-base md:text-lg max-w-3xl" : "text-sm"}`}>
        {c.desc}
      </p>

      {/* Services */}
      {c.services && c.services.length > 0 && (
        <div className="mt-6 pt-6 border-t border-brand-border">
          <p className="text-[10px] uppercase tracking-[0.28em] text-brand-gold mb-3">Services delivered</p>
          <ul className={`grid gap-2 ${featured ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
            {c.services.map((s) => (
              <li key={s} className="flex items-start gap-2 text-sm text-brand-charcoal/85">
                <Check size={14} className="text-brand-gold mt-0.5 shrink-0" />
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* subtle bottom accent */}
      <span className="absolute bottom-0 left-0 right-0 h-px bg-brand-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-700" />
    </motion.article>
  );
}

export default function Clients() {
  const featured = CLIENTS.find((c) => c.featured);
  const rest = CLIENTS.filter((c) => !c.featured);

  return (
    <section
      id="clients"
      data-testid="clients-section"
      className="relative py-24 md:py-40 border-t border-brand-border bg-brand-beige"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-6">(05) — Our Client Partnerships</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-medium text-brand-charcoal">
              Trusted by <span className="italic text-brand-gold">Government, Healthcare, FinTech</span> &amp; Leading Consumer Brands.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4 text-brand-charcoal/75 text-base md:text-lg leading-relaxed">
            We build long-term partnerships by delivering creative solutions, strategic marketing and measurable business growth.
          </div>
        </div>

        {/* Featured client */}
        {featured && (
          <div className="mb-6 md:mb-8">
            <ClientCard c={featured} i={0} featured />
          </div>
        )}

        {/* Rest grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {rest.map((c, i) => (
            <ClientCard key={c.name} c={c} i={i} />
          ))}
        </div>

        {/* Tagline strip */}
        <div className="mt-16 md:mt-20 pt-8 border-t border-brand-border flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="font-display italic text-2xl md:text-3xl text-brand-charcoal max-w-3xl leading-snug">
            Building long-term partnerships through creativity, strategy and measurable results.
          </p>
          <a
            href="#contact"
            data-testid="clients-cta"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.24em] font-semibold text-brand-gold hover:text-brand-charcoal transition"
          >
            Become a client →
          </a>
        </div>
      </div>
    </section>
  );
}
